import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// const API_URL = "";

const getConfig = (getState) => ({
  withCredentials: true,
  headers: {
    Authorization: `Bearer ${getState().userLogin?.userInfo?.token}`,
  },
});

// ===================================================================
// Thunks
// ===================================================================
export const fetchQuote = createAsyncThunk(
  "quotes/fetch",
  async (id, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/quotes/${id}`, getConfig(getState));
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error?.message || "ไม่สามารถโหลดใบเสนอราคาได้"
      );
    }
  }
);

export const fetchJobQuotes = createAsyncThunk(
  "quotes/fetchByJob",
  async (jobId, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `/api/jobs/${jobId}/quotes`,
        getConfig(getState)
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error?.message || "ไม่สามารถโหลดใบเสนอราคาของงานนี้ได้"
      );
    }
  }
);

export const fetchAllQuotes = createAsyncThunk(
  "quotes/fetchAll",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        `/api/quotes`,
        getConfig(getState)
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error?.message || "ไม่สามารถโหลดใบเสนอราคาทั้งหมดได้"
      );
    }
  }
);

export const createQuote = createAsyncThunk(
  "quotes/create",
  async ({ jobId, payload }, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `/api/jobs/${jobId}/quotes`,
        payload,
        getConfig(getState)
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error?.message || "สร้างใบเสนอราคาไม่สำเร็จ"
      );
    }
  }
);

export const updateQuote = createAsyncThunk(
  "quotes/update",
  async ({ id, payload }, { getState, rejectWithValue }) => {
    try {
      await axios.put(`/api/quotes/${id}`, payload, getConfig(getState));
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error?.message || "อัปเดตใบเสนอราคาไม่สำเร็จ"
      );
    }
  }
);

export const sendQuote = createAsyncThunk(
  "quotes/send",
  async (id, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `/api/quotes/${id}/send`,
        {},
        getConfig(getState)
      );
      return { id, ...data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error?.message || "ส่งใบเสนอราคาไม่สำเร็จ"
      );
    }
  }
);

// 🆕 Customer accepts the work — moves job + quote to APPROVED
export const customerAccepts = createAsyncThunk(
  "quotes/customerAccepts",
  async ({ jobId, note }, { getState, rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `/api/jobs/${jobId}/customer-accepts`,
        { note },
        getConfig(getState)
      );
      return data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error?.message || "ไม่สามารถยืนยันการอนุมัติได้"
      );
    }
  }
);

// ===================================================================
// Slice
// ===================================================================
const slice = createSlice({
  name: "quotes",
  initialState: {
    current: null,
    list: [],
    loading: false,
    error: null,
    lastSentPdf: null,
  },
  reducers: {
    clearCurrent: (s) => {
      s.current = null;
      s.lastSentPdf = null;
    },
    clearError: (s) => {
      s.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchQuote
      .addCase(fetchQuote.pending, (s) => {
        s.loading = true;
        s.error = null;
      })
      .addCase(fetchQuote.fulfilled, (s, a) => {
        s.loading = false;
        s.current = a.payload;
      })
      .addCase(fetchQuote.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      // fetchJobQuotes
      .addCase(fetchJobQuotes.fulfilled, (s, a) => {
        s.list = a.payload;
      })
      // fetchAllQuotes
      .addCase(fetchAllQuotes.pending, (s) => {
        s.loading = true;
      })
      .addCase(fetchAllQuotes.fulfilled, (s, a) => {
        s.loading = false;
        s.list = a.payload;
      })
      .addCase(fetchAllQuotes.rejected, (s, a) => {
        s.loading = false;
        s.error = a.payload;
      })
      // createQuote
      .addCase(createQuote.fulfilled, (s, a) => {
        s.list.unshift(a.payload);
        s.current = { ...a.payload, items: [] };
      })
      .addCase(createQuote.rejected, (s, a) => {
        s.error = a.payload;
      })
      // updateQuote
      .addCase(updateQuote.fulfilled, (s) => {
        s.current = { ...s.current, status: "DRAFT" };
      })
      .addCase(updateQuote.rejected, (s, a) => {
        s.error = a.payload;
      })
      // sendQuote
      .addCase(sendQuote.fulfilled, (s, a) => {
        if (s.current?.id === a.payload.id) {
          s.current.status = "SENT";
          s.current.sent_at = new Date().toISOString();
        }
        s.lastSentPdf = a.payload.pdf_url;
      })
      .addCase(sendQuote.rejected, (s, a) => {
        s.error = a.payload;
      })
      // customerAccepts
      .addCase(customerAccepts.fulfilled, (s, a) => {
        if (s.current) {
          s.current.status = "APPROVED";
        }
        s.successMessage = "ลูกค้าอนุมัติงานเรียบร้อย — ส่งต่อให้ PM วางแผน";
      })
      .addCase(customerAccepts.rejected, (s, a) => {
        s.error = a.payload;
      });
  },
});

export const { clearCurrent, clearError } = slice.actions;
export default slice.reducer;
